'use client';

import SectionCard from '@/app/(website)/components/SectionCard';
import SectionHeader from '@/app/(website)/components/SectionHeader';
import Book1 from '@/assets/icons/Book-1.svg';
import Book2 from '@/assets/icons/Book-2.svg';
import Details from '@/assets/icons/Details.svg';
import Scale from '@/assets/icons/Scale.svg';
import {useEffect, useRef, useState} from 'react';
import Image from 'next/image';
import {Body} from '@/components/body';
import {cn} from '@/lib/utils';

const sources = [
  {
    icon: <Scale width={64} height={64} />,
    title: 'Codice dei Contratti Pubblici',
    description:
      'D.Lgs. 36/2023, decreti attuativi e disposizioni transitorie aggiornate quotidianamente per verificare procedure, soglie e adempimenti.',
  },
  {
    icon: <Book1 width={64} height={64} />,
    title: 'Delibere e Linee Guida ANAC',
    description:
      "Delibere, bandi tipo, linee guida e pareri di precontenzioso dell'Autorità Nazionale Anticorruzione, costantemente aggiornati.",
  },
  {
    icon: <Details width={64} height={64} />,
    title: 'Fonti Personali',
    description:
      'Possibilità di integrare la conoscenza di normo con le proprie fonti personali sia caricando i documenti che permettendo il collegamento tra Normo.ai e siti Web terzi.',
  },
  {
    icon: <Book2 width={64} height={64} />,
    title: 'Normativa UE e CGUE',
    description:
      "Direttive 2014/23-24-25/UE e giurisprudenza della Corte di Giustizia dell'Unione Europea per il recepimento nazionale.",
  },
];

export default function SourcesAppalti() {
  const [isVisible, setIsVisible] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {threshold: 0.1},
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => {
      if (gridRef.current) {
        observer.unobserve(gridRef.current);
      }
    };
  }, []);

  const getClass = (index: number) => {
    const delays = ['', 'delay-200', 'delay-500', 'delay-700'];
    return `transition-all duration-1000 ease-in-out transform ${delays[index] ?? ''} ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
    }`;
  };

  return (
    <div id="sources" className="flex flex-col items-center gap-10 py-16 px-8">
      <SectionHeader
        badgeText="Fonti"
        title={
          <>
            Fonti{' '}
            <span className="rounded-[2rem] rounded-br-[8px] py-0 px-2 md:px-4 bg-secondary text-primary">
              informative
            </span>{' '}
            per gli appalti pubblici
          </>
        }
        subtitle="Normo.ai utilizza fonti normative e giurisprudenziali specializzate per offrire una base di lavoro affidabile, aggiornata e concretamente utile nella gestione delle procedure di gara."
        titleAnimationStart="bottom"
      />
      <div
        className="grid grid-cols-1 md:grid-cols-2 max-w-[912px] gap-8"
        ref={gridRef}
      >
        {sources.map((source, index) => (
          <SectionCard
            icon={source.icon}
            title={source.title}
            text={source.description}
            key={index}
            className={getClass(index)}
          />
        ))}
      </div>
      <div
        className={cn(
          'flex flex-col items-center md:flex-row md:items-start gap-4 max-w-[912px] transition-all duration-1000 ease-in-out transform delay-1000',
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40',
        )}
      >
        <Image
          src="/ec.png"
          alt="Certificazione Euroconference"
          width={100}
          height={100}
        />
        <div className="flex flex-col">
          <Body variant="small">Certificazione Autorale Euroconference</Body>
          <Body variant="small" className="font-normal">
            Processo continuo di verifica e validazione da parte di un Comitato
            di Esperti Euroconference che garantisce correttezza, completezza e
            rigore dei contenuti.
          </Body>
        </div>
      </div>
    </div>
  );
}
